import { markRaw } from "vue";
import { fetchAllSamples, fetchMetadataTables } from "@/features/eda/api";
import { buildEdaDatasets } from "@/features/eda/core/datasets";
import { cacheKey, consumePageReload, readCachedPayload, writeCachedPayload } from "@/features/eda/cache";

const sessionKey = (tenantId) => `${tenantId}|${localStorage.getItem("authToken") || ""}`;

let inflight = null;

export default {
  namespaced: true,

  state: {
    status: "idle",
    error: null,
    sessionKey: null,
    progress: { loaded: 0, total: null },
    datasets: null,
    metadataTables: null,
    loadedAt: null,
    fromCache: false,
    analysisStates: {},
  },

  getters: {
    isReady: (state) => state.status === "ready",
    analysisState: (state) => (analysisId) => state.analysisStates[analysisId] || null,
  },

  mutations: {
    loadStarted(state, key) {
      if (state.sessionKey !== key) state.analysisStates = {};
      state.status = "loading";
      state.error = null;
      state.sessionKey = key;
      state.progress = { loaded: 0, total: null };
      state.datasets = null;
      state.metadataTables = null;
    },
    loadProgress(state, progress) {
      state.progress = progress;
    },
    loadSucceeded(state, { datasets, metadataTables, loadedAt, fromCache }) {
      state.datasets = markRaw(datasets);
      state.metadataTables = markRaw(metadataTables || []);
      state.loadedAt = loadedAt;
      state.fromCache = fromCache;
      state.status = "ready";
    },
    loadFailed(state, error) {
      state.status = "error";
      state.error = error;
    },
    setAnalysisState(state, { analysisId, value }) {
      state.analysisStates = { ...state.analysisStates, [analysisId]: value };
    },
    clear(state) {
      state.status = "idle";
      state.error = null;
      state.sessionKey = null;
      state.datasets = null;
      state.metadataTables = null;
      state.loadedAt = null;
      state.fromCache = false;
      state.analysisStates = {};
    },
  },

  actions: {
    async loadData({ state, commit }, { tenantId, force = false } = {}) {
      const key = sessionKey(tenantId);
      const pageReloaded = consumePageReload();
      if (!force && state.sessionKey === key) {
        if (state.status === "ready") return;
        if (state.status === "loading" && inflight) return inflight;
      }

      commit("loadStarted", key);
      const run = (async () => {
        try {
          const storageKey = cacheKey(tenantId);
          const cached = force || pageReloaded ? null : await readCachedPayload(storageKey);
          if (state.sessionKey !== key) return;
          if (cached) {
            commit("loadSucceeded", {
              datasets: buildEdaDatasets(cached.samples, cached.metadataTables),
              metadataTables: cached.metadataTables,
              loadedAt: cached.savedAt,
              fromCache: true,
            });
            return;
          }

          const [samples, metadataTables] = await Promise.all([
            fetchAllSamples({ tenantId, onProgress: (p) => commit("loadProgress", p) }),
            fetchMetadataTables(tenantId),
          ]);
          if (state.sessionKey !== key) return;
          commit("loadSucceeded", {
            datasets: buildEdaDatasets(samples, metadataTables),
            metadataTables,
            loadedAt: Date.now(),
            fromCache: false,
          });
          writeCachedPayload(storageKey, { samples, metadataTables });
        } catch (error) {
          if (state.sessionKey !== key) return;
          console.error("EDA data load failed:", error);
          commit("loadFailed", (error && error.message) || String(error));
        } finally {
          if (inflight === run) inflight = null;
        }
      })();
      inflight = run;
      return run;
    },

    saveAnalysisState({ commit }, payload) {
      commit("setAnalysisState", payload);
    },
  },
};
